import { Select, Spin } from 'antd';
import type { SelectProps } from 'antd/es/select';
import React, { useMemo, useRef, useState } from 'react';

export interface DebounceSelectProps<ValueType = any>
    extends Omit<SelectProps<ValueType | ValueType[]>, 'options' | 'children'> {
    fetchOptions: (search: string) => Promise<ValueType[]>;
    debounceTimeout?: number;
    minInputLength?: number;
}

function DebounceSelect<
    ValueType extends {
        key?: string;
        label: React.ReactNode;
        value: string | number;
    } = any
>({
    fetchOptions,
    debounceTimeout = 800,
    minInputLength = 3,
    ...props
}: DebounceSelectProps<ValueType>) {
    const [fetching, setFetching] = useState(false);
    const [options, setOptions] = useState<ValueType[]>([]);
    const fetchRef = useRef(0);

    const debounce = (cb:any, delay = 250) => {
        let timeout: any;
      
        return (...args: any[]) => {
          clearTimeout(timeout)
          timeout = setTimeout(() => {
            cb(...args);
          }, delay);
        }
      };

    const debounceFetcher = useMemo(() => {
        const loadOptions = (value: string) => {
            if (minInputLength && value.length < minInputLength) {
                return;
            }

            fetchRef.current += 1;
            const fetchId = fetchRef.current;
            setOptions([]);
            setFetching(true);

            fetchOptions(value).then((newOptions) => {
                if (fetchId !== fetchRef.current) {
                    // for fetch callback order
                    return;
                }

                setOptions(newOptions);
                setFetching(false);
            });
        };

        return debounce(loadOptions, debounceTimeout);
    }, [fetchOptions, debounceTimeout, minInputLength]);

    return (
        <Select
            labelInValue
            filterOption={false}
            onSearch={debounceFetcher}
            showSearch
            notFoundContent={fetching ? <Spin size="small" /> : null}
            {...props}
            options={options}
        />
    );
}

export default DebounceSelect;
